import { Add, Delete, Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FlexGrid,
  FlexGridCellTemplate,
  FlexGridColumn,
} from "@grapecity/wijmo.react.grid";
import { FlexGridFilter } from "@grapecity/wijmo.react.grid.filter";
import { FlexGridXlsxConverter } from "@grapecity/wijmo.grid.xlsx";
import { useEffect, useMemo, useRef, useState } from "react";
import excel from "src/assets/images/excelIcon.png";
import SimpleColumnPicker from "src/qubCommon/Grids/SimpleColumnPicker";

const rowActionButtonSx = {
  height: 26,
  width: 26,
};

const currencyFormatter = new Intl.NumberFormat("el-GR", {
  currency: "EUR",
  style: "currency",
});

const formatCurrency = (value) => currencyFormatter.format(Number(value ?? 0));

const formatValue = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  return value;
};

const ReadOnlyCell = ({ value, align = "left", muted = false }) => (
  <Box
    sx={{
      alignItems: "center",
      color: muted ? "#667085" : "#111827",
      display: "flex",
      fontSize: 13,
      fontWeight: 400,
      height: "100%",
      justifyContent: align === "right" ? "flex-end" : "flex-start",
      overflow: "hidden",
      textAlign: align,
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
    title={value ?? ""}
  >
    {formatValue(value)}
  </Box>
);

const sumPaymentAmounts = (payments = []) =>
  payments.reduce((sum, payment) => sum + Number(payment?.amount ?? 0), 0);

const ExpensePaymentsGrid = ({
  transaction,
  payments = transaction?.payments ?? [],
  addPaymentSetting = transaction?.settings?.addPayment,
  isAddPaymentDisabled = false,
  onAddPayment,
  onPaymentAction,
}) => {
  const gridRef = useRef(null);
  const [columnPickerRefreshKey, setColumnPickerRefreshKey] = useState(0);
  const hasRowActions = useMemo(
    () =>
      payments.some(
        (payment) => payment?.canEdit === true || payment?.canDelete === true,
      ),
    [payments],
  );
  const canAddPayment =
    addPaymentSetting?.can === true || transaction?.canAddPayment === true;
  const addPaymentText = addPaymentSetting?.txt ?? "Προσθήκη Πληρωμής";
  const paidAmount = transaction?.paidAmount ?? sumPaymentAmounts(payments);

  useEffect(() => {
    if (!gridRef.current) return;

    setColumnPickerRefreshKey((currentKey) => currentKey + 1);
  }, [hasRowActions]);

  const handleGridInitialized = (grid) => {
    gridRef.current = grid;
    setColumnPickerRefreshKey((currentKey) => currentKey + 1);
  };

  const handleExportExcel = () => {
    if (!gridRef.current) return;

    FlexGridXlsxConverter.saveAsync(
      gridRef.current,
      {
        includeColumnHeaders: true,
        includeStyles: false,
      },
      "ExpensePayments.xlsx",
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "0.25rem",
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        p: 1,
        position: "relative",
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <SimpleColumnPicker
            ref={gridRef}
            refreshKey={columnPickerRefreshKey}
          />
          <Box boxShadow={2} sx={{ borderRadius: 50, height: 40, width: 40 }}>
            <Tooltip title="Εξαγωγή σε Excel">
              <IconButton onClick={handleExportExcel}>
                <img src={excel} alt="excel" style={{ height: 24 }} />
              </IconButton>
            </Tooltip>
          </Box>
          {canAddPayment && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add />}
              disabled={isAddPaymentDisabled}
              onClick={() => onAddPayment?.()}
              title={`${addPaymentText} (Alt+P)`}
              sx={{
                fontWeight: 800,
                minHeight: 32,
                whiteSpace: "nowrap",
              }}
            >
              {addPaymentText}
            </Button>
          )}
        </Box>
        <Typography
          sx={{
            color: "#101828",
            fontSize: 14,
            fontWeight: 500,
            ml: "auto",
            textAlign: "right",
            whiteSpace: "nowrap",
          }}
        >
          Σύνολο πληρωμών: {formatCurrency(paidAmount)}
        </Typography>
      </Box>

      <FlexGrid
        autoGenerateColumns={false}
        className="explorer-grid"
        headersVisibility="Column"
        initialized={handleGridInitialized}
        isReadOnly
        itemsSource={payments}
        selectionMode="Row"
        style={{}}
      >
        <FlexGridFilter />
        {hasRowActions && (
          <FlexGridColumn
            header=""
            width={78}
            minWidth={78}
            align="center"
            allowSorting={false}
          >
            <FlexGridCellTemplate
              cellType="Cell"
              template={(ctx) => (
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: 0.25,
                    height: "100%",
                    justifyContent: "center",
                  }}
                >
                  {ctx.item?.canEdit === true && (
                    <Tooltip title="Επεξεργασία πληρωμής">
                      <IconButton
                        size="small"
                        color="primary"
                        sx={rowActionButtonSx}
                        onClick={(event) => {
                          event.stopPropagation();
                          onPaymentAction?.("editPayment", ctx.item);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {ctx.item?.canDelete === true && (
                    <Tooltip title="Διαγραφή πληρωμής">
                      <IconButton
                        size="small"
                        color="error"
                        sx={rowActionButtonSx}
                        onClick={(event) => {
                          event.stopPropagation();
                          onPaymentAction?.("deletePayment", ctx.item);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
            />
          </FlexGridColumn>
        )}
        <FlexGridColumn
          binding="transTypeName"
          header="Είδος Πληρωμής"
          width={220}
          minWidth={180}
        >
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx) => (
              <ReadOnlyCell
                value={ctx.item?.transTypeName ?? ctx.item?.transTypeAbbr}
              />
            )}
          />
        </FlexGridColumn>
        <FlexGridColumn
          binding="paymentDateFormatted"
          header="Ημ/νία Πληρωμής"
          width={150}
          align="right"
        >
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx) => (
              <ReadOnlyCell
                value={ctx.item?.paymentDateFormatted}
                align="right"
              />
            )}
          />
        </FlexGridColumn>
        <FlexGridColumn
          binding="paymentNumber"
          header="Αριθμός"
          width={150}
          minWidth={120}
        >
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx) => <ReadOnlyCell value={ctx.item?.paymentNumber} />}
          />
        </FlexGridColumn>
        <FlexGridColumn
          binding="amount"
          header="Ποσό"
          format="c€"
          width={140}
          align="right"
        />
        <FlexGridColumn
          binding="comments"
          header="Σχόλια"
          width={220}
          minWidth={180}
        >
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx) => <ReadOnlyCell value={ctx.item?.comments} />}
          />
        </FlexGridColumn>
        <FlexGridColumn
          binding="remarks"
          header="Παρατηρήσεις"
          width="*"
          minWidth={220}
        >
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx) => <ReadOnlyCell value={ctx.item?.remarks} />}
          />
        </FlexGridColumn>
      </FlexGrid>

      {payments.length === 0 && (
        <Typography
          sx={{
            color: "#6b7280",
            fontSize: 14,
            fontWeight: 400,
            textAlign: "center",
          }}
        >
          Δεν έχουν καταχωρηθεί πληρωμές.
        </Typography>
      )}
    </Box>
  );
};

export default ExpensePaymentsGrid;
